// src/pages/modals/POS_VentasPendientes.js
'use strict';

import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  Restore as RestoreIcon,
  ReceiptLong as ReceiptLongIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';

import PinConfirmModal from './PinConfirmModal';

const formatter = new Intl.NumberFormat('es-CL', {
  style: 'currency',
  currency: 'CLP',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatMoney(value) {
  const n = Number(value);
  return formatter.format(Number.isFinite(n) ? n : 0);
}

function formatDate(value) {
  if (!value) return 'Sin fecha';

  try {
    return new Date(value).toLocaleString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Sin fecha';
  }
}

function getNombreCliente(cliente) {
  if (!cliente) return 'Cliente genérico';

  if (cliente.nombres && cliente.apellidos) {
    return `${cliente.nombres} ${cliente.apellidos}`;
  }

  if (cliente.razon_social) return cliente.razon_social;
  if (cliente.fullname) return cliente.fullname;
  if (cliente.name) return cliente.name;

  return 'Cliente genérico';
}

function getRutCliente(cliente) {
  if (!cliente) return '';

  const rut = cliente.rut || '';
  const dv = cliente.dv || '';

  if (rut && dv && !String(rut).includes('-')) {
    return `${rut}-${dv}`;
  }

  return rut;
}

function getCantidadItems(items) {
  if (!Array.isArray(items)) return 0;
  return items.length;
}

function getResumenProductos(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return 'Sin productos';
  }

  const primeros = items
    .slice(0, 3)
    .map((item) => {
      const nombre = item.name || item.nombre || 'Producto';

      const cantidad = item.isWeight
        ? `${Number(item.quantity || 0).toFixed(3)} kg`
        : `x${Number(item.quantity || 0)}`;

      return `${nombre} ${cantidad}`;
    })
    .join(' · ');

  if (items.length > 3) {
    return `${primeros} · +${items.length - 3} más`;
  }

  return primeros;
}

const POS_VentasPendientes = ({
  open,
  ventas = [],
  onClose,
  onRetomar,
  onEliminar,
  onLimpiarTodo,
}) => {
  const [pinOpen, setPinOpen] = useState(false);
  const [accionPendiente, setAccionPendiente] = useState(null);

  const ventasOrdenadas = useMemo(() => {
    return [...(Array.isArray(ventas) ? ventas : [])].sort((a, b) => {
      const da = new Date(a.fecha_actualizacion || a.fecha_creacion || 0).getTime();
      const db = new Date(b.fecha_actualizacion || b.fecha_creacion || 0).getTime();
      return db - da;
    });
  }, [ventas]);

  const cerrarPin = useCallback(() => {
    setPinOpen(false);
    setAccionPendiente(null);
  }, []);

  const solicitarEliminarVenta = useCallback((venta) => {
    if (!venta) return;

    setAccionPendiente({
      tipo: 'eliminar',
      venta,
    });

    setPinOpen(true);
  }, []);

  const solicitarLimpiarTodo = useCallback(() => {
    if (!ventasOrdenadas.length) return;

    setAccionPendiente({
      tipo: 'limpiar_todo',
      venta: null,
    });

    setPinOpen(true);
  }, [ventasOrdenadas.length]);

  const ejecutarAccionAutorizada = useCallback(() => {
    if (!accionPendiente) {
      setPinOpen(false);
      return;
    }

    if (accionPendiente.tipo === 'eliminar' && accionPendiente.venta) {
      onEliminar?.(accionPendiente.venta);
    }

    if (accionPendiente.tipo === 'limpiar_todo') {
      onLimpiarTodo?.();
    }

    setPinOpen(false);
    setAccionPendiente(null);
  }, [accionPendiente, onEliminar, onLimpiarTodo]);

  const handleClosePrincipal = useCallback(() => {
    setPinOpen(false);
    setAccionPendiente(null);
    onClose?.();
  }, [onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClosePrincipal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            bgcolor: 'grey.50',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <ReceiptLongIcon />
            </Box>

            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                Ventas en espera
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Retoma una venta pausada o elimina con PIN de autorización.
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Cerrar">
            <IconButton onClick={handleClosePrincipal}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ p: 2.5 }}>
          {ventasOrdenadas.length === 0 ? (
            <Alert severity="info">
              No hay ventas en espera. Cuando un cliente necesite agregar algo, usa
              <strong> F6 </strong>
              para dejar la venta actual pausada.
            </Alert>
          ) : (
            <Stack spacing={1.5}>
              <Alert severity="warning" icon={<WarningAmberIcon />}>
                Estas ventas están guardadas solo como borradores locales del POS.
                No generan venta, pago, DTE ni descuento definitivo de stock hasta que se retomen y se paguen.
              </Alert>

              {ventasOrdenadas.map((venta) => {
                const cliente = venta.cliente || null;
                const items = Array.isArray(venta.items) ? venta.items : [];

                const total =
                  venta.totales?.total_pagar ??
                  venta.totales?.totalPayable ??
                  venta.totales?.total_bruto ??
                  0;

                const folio = venta.folio || venta.id;
                const tipoDocumento = venta.tipo_documento || 'boleta';
                const origen = venta.origen_importado || null;

                return (
                  <Card
                    key={venta.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 900,
                                lineHeight: 1.2,
                              }}
                            >
                              {getNombreCliente(cliente)}
                            </Typography>

                            <Chip
                              size="small"
                              label={tipoDocumento === 'factura' ? 'Factura' : 'Boleta'}
                              color={tipoDocumento === 'factura' ? 'primary' : 'default'}
                              variant="outlined"
                            />

                            {origen?.id && (
                              <Chip
                                size="small"
                                label={`${origen.type === 'pedido' ? 'Pedido' : 'Cotización'} #${origen.id}`}
                                color="info"
                                variant="outlined"
                              />
                            )}
                          </Stack>

                          <Typography variant="body2" color="text.secondary">
                            {getRutCliente(cliente) || 'Sin RUT informado'}
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              mt: 1,
                              color: 'text.primary',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {getResumenProductos(items)}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            sx={{ mt: 1 }}
                          >
                            <Chip
                              size="small"
                              label={`${getCantidadItems(items)} producto(s)`}
                            />

                            <Chip
                              size="small"
                              label={`Guardada: ${formatDate(venta.fecha_creacion)}`}
                            />

                            {folio && (
                              <Chip
                                size="small"
                                label={folio}
                                variant="outlined"
                              />
                            )}
                          </Stack>
                        </Box>

                        <Divider
                          flexItem
                          orientation="vertical"
                          sx={{
                            display: { xs: 'none', sm: 'block' },
                          }}
                        />

                        <Box
                          sx={{
                            minWidth: { xs: '100%', sm: 190 },
                            textAlign: { xs: 'left', sm: 'right' },
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Total estimado
                          </Typography>

                          <Typography
                            variant="h5"
                            sx={{
                              fontWeight: 900,
                              lineHeight: 1.1,
                              mb: 1.5,
                            }}
                          >
                            {formatMoney(total)}
                          </Typography>

                          <Stack
                            direction={{ xs: 'row', sm: 'column' }}
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              variant="contained"
                              startIcon={<RestoreIcon />}
                              onClick={() => onRetomar?.(venta)}
                              fullWidth
                            >
                              Retomar
                            </Button>

                            <Button
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => solicitarEliminarVenta(venta)}
                              fullWidth
                            >
                              Eliminar con PIN
                            </Button>
                          </Stack>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            justifyContent: 'space-between',
          }}
        >
          <Button
            color="error"
            disabled={ventasOrdenadas.length === 0}
            onClick={solicitarLimpiarTodo}
          >
            Limpiar todas con PIN
          </Button>

          <Button variant="contained" onClick={handleClosePrincipal}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <PinConfirmModal
        open={pinOpen}
        onClose={cerrarPin}
        onVerified={ejecutarAccionAutorizada}
      />
    </>
  );
};

export default POS_VentasPendientes;